import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage } from '@tmapy/intl';

import {
  DangerAlert,
  WaitingAlert,
  FormRow,
  FormItem,
  BtnBar,
  CtaBtn,
  Input,
  Form,
  SuccessAlert,
} from '@tmapy/style-guide';

import { useAuth, useFormItemServerErrors, useInput, useSelector } from '../../hooks';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';

import type { PatchedSubjektUpdateRequest } from '../../openapi/types/PatchedSubjektUpdate';
import {
  SubjektyRetrieveResponse,
  useSubjektyRetrieveResource,
} from '../../openapi/operations/subjektyRetrieve';
import { subjektyPartialUpdate } from '../../openapi/operations/subjektyPartialUpdate';
import { InputProps } from '../../hooks/useInput';
import {
  subjectBtnMessages,
  subjectMessages,
  subjectPersonMessages,
  subjectQueryMessages,
} from './subjectMessages';
import { useNazev, useRegistry } from './subjectHooks';

type DetailProps = {
  subjectId: string;
};

export const SubjectDetail: React.FC<DetailProps> = React.memo((props) => {
  const auth = useAuth();
  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const subjectId = parseInt(props.subjectId, 10);

  const resource = useSubjektyRetrieveResource(subjectId ? { id: subjectId } : null);
  const data = useMemo(() => {
    return (resource.data ? { ...resource.data } : {}) as SubjektyRetrieveResponse;
  }, [resource.data]);

  const formatMessage = useMessage();

  const registryItem = useRegistry(data.registry);
  const nazevItem = useNazev({
    typ_osoby: data.typ_osoby,
    nazev: data.nazev,
    jmeno: data.jmeno,
    prijmeni: data.prijmeni,
  });

  // ACL
  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('Subjekty', 'partial_update', data),
      subjectUpdate: auth.hasPermission('Subjekty', 'update', data),
    }),
    [auth, data],
  );

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
  }, [setSaveQueryState]);

  // NOTES
  const note = useInput<InputProps>({
    name: 'note',
    isDisabled: !acl.canUpdate,
    initialValue: data.poznamka,
    label: formatMessage(subjectMessages.note),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.note,
  });

  // ACTIONS
  const handleSave = useCallback(async () => {
    const values: PatchedSubjektUpdateRequest = {
      poznamka: note.value || '',
    };

    setSaveQueryState(QUERY_STATE.WAITING);

    try {
      const response = await subjektyPartialUpdate({
        query: { id: subjectId },
        request: values,
      });

      if (response.ok) {
        setSaveQueryState(QUERY_STATE.SUCCESS);
      } else {
        setSaveQueryState(QUERY_STATE.FAIL);
      }
    } catch (e) {
      setSaveQueryState(QUERY_STATE.FAIL);
    }
    return null;
  }, [note]);

  return (
    <MainContent width='full'>
      <article className='sg-u-vs-4'>
        <div className='dmx-doc--head sg-u-vs-2'>
          {resource.queryState === QUERY_STATE.SUCCESS && (
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-b sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-u-hsl-1'>
                <PageTitle>
                  <FormattedMessage
                    {...subjectMessages.subjectDetailTitle}
                    values={{ id: data.id || 'empty' }}
                  />
                </PageTitle>
              </header>
              <BtnBar>
                {acl.subjectUpdate && (
                  <CtaBtn onClick={handleSave}>
                    <FormattedMessage {...subjectBtnMessages.save} />
                  </CtaBtn>
                )}
              </BtnBar>
            </div>
          )}

          {(saveQueryState === QUERY_STATE.WAITING ||
            resource.queryState === QUERY_STATE.WAITING) && (
            <WaitingAlert>
              <FormattedMessage {...subjectQueryMessages.submitQueryStateWaiting} />
            </WaitingAlert>
          )}

          {resource.queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...subjectQueryMessages.loadQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...subjectQueryMessages.submitQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.SUCCESS && (
            <SuccessAlert>
              <FormattedMessage {...subjectQueryMessages.submitQueryStateSuccess} />
            </SuccessAlert>
          )}
        </div>
        {resource.queryState === QUERY_STATE.SUCCESS && (
          <div className='sg-u-vs-4'>
            <Form onSubmit={handleSave}>
              <div className='sg-u-box sg-a-p-2 sg-a-d-f sg-a-fw-w sg-a-g-2'>
                <div className='sg-a-f-a sg-u-vs-1'>
                  <FormRow>
                    <FormItem label={formatMessage(subjectMessages.name)}>{nazevItem}</FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(subjectMessages.person)}>
                      {data.typ_osoby
                        ? formatMessage.lookup(subjectPersonMessages, data.typ_osoby)
                        : ''}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(subjectMessages.ico)}>{data.ico}</FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(subjectMessages.id)}>
                      {data.id_subjektu}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(subjectMessages.address)}>
                      {data.adresa}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(subjectMessages.registry)}>
                      {registryItem.join(', ')}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem {...note}>
                      <Input {...note} />
                    </FormItem>
                  </FormRow>
                </div>
              </div>
            </Form>
          </div>
        )}
      </article>
    </MainContent>
  );
});
